import type { AuthError, Session, User } from '@supabase/supabase-js';
import React, { type ReactNode, useEffect, useMemo, useState } from 'react';

import type { AuthContextType } from './AuthContextDefinition';
import { AuthContext } from './AuthContextDefinition'; // Import from definition file

import { idbPersister } from '@/config/queryClient';
import { supabase } from '@/integrations/supabase/client';
import { type LaunchAttribution, captureLaunchAttribution } from '@/lib/analytics/launch-lifecycle';
import { clearSidebarUpgradeDismissed } from '@/lib/sidebar-upgrade-dismiss';
import { AuthRecovery } from '@/utils/authRecovery';
import { logger } from '@/utils/logger';

// Define a type for JSON-compatible values for user metadata
type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

// Environment configuration
const isDevelopment = import.meta.env.MODE === 'development';
const LAUNCH_ATTRIBUTION_STORAGE_KEY = 'mora:launch-attribution';

function getBrowserLaunchAttribution(): LaunchAttribution {
  return captureLaunchAttribution(window.location.href, document.referrer);
}

const LOCAL_DRAFT_KEY_PREFIX = 'mora_draft_';

function clearLocalDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(LOCAL_DRAFT_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    logger.warn('[auth] could not clear local drafts on sign-out', err);
  }
}

function persistPendingOAuthAttribution(): void {
  try {
    window.sessionStorage.setItem(
      LAUNCH_ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(getBrowserLaunchAttribution()),
    );
  } catch {
    // Attribution is supplemental to account creation. A privacy-restricted
    // browser must result in an explicit unknown source, never a failed login.
  }
}

async function capturePendingOAuthAttribution(accessToken: string): Promise<void> {
  try {
    const serialized = window.sessionStorage.getItem(LAUNCH_ATTRIBUTION_STORAGE_KEY);
    if (!serialized) return;
    const attribution = JSON.parse(serialized) as LaunchAttribution;
    const response = await fetch('/api/analytics/launch-attribution', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(attribution),
    });
    if (response.ok) window.sessionStorage.removeItem(LAUNCH_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // The server permits this handoff only once and only for a fresh user. Do
    // not make session establishment depend on analytics availability.
  }
}

// Removed AuthError type (assuming it's not needed or defined elsewhere if still used)
// Removed AuthContextType interface definition
// Removed AuthContext definition

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let subscription: any = null;
    let sessionCheckTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

    try {
      // Set up auth state listener (non-blocking)
      const {
        data: { subscription: authSubscription },
      } = supabase.auth.onAuthStateChange((event, newSession) => {
        if (event === 'SIGNED_OUT') {
          clearSidebarUpgradeDismissed();
          // Belt-and-suspenders for the cross-tenant cache-leak class (P0,
          // 2026-07-23): the app's QueryClient is a module singleton created
          // once in main.tsx and persisted to IndexedDB for 24h
          // (PersistQueryClientProvider, maxAge: 1000*60*60*24). Any analytics
          // hook whose queryKey omits the user/owner id would otherwise keep
          // serving the outgoing user's cached data to whoever signs into
          // this browser next. Clearing the entire cache on sign-out closes
          // that class outright, in addition to the per-hook queryKey fixes.
          // main.tsx exposes the same client instance on `window.queryClient`
          // for error recovery — reused here rather than re-instantiating a
          // client, since importing main.tsx directly would be circular
          // (main.tsx already imports AuthProvider from this file).
          const globalQueryClient = (window as unknown as { queryClient?: { clear?: () => void } })
            .queryClient;
          if (typeof globalQueryClient?.clear === 'function') {
            globalQueryClient.clear();
          }
          clearLocalDrafts();
          idbPersister.removeClient().catch((err: unknown) => {
            logger.warn('[auth] could not remove persisted query cache on sign-out', err);
          });
        }
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          AuthRecovery.invalidate();
        }
        if (event === 'SIGNED_IN' && newSession?.access_token) {
          // Best-effort merge any anon Discover prefs into the user row.
          // No-op if no mora_anon cookie or no anon row exists.
          fetch('/api/discover/preferences/migrate-anon', {
            method: 'POST',
            credentials: 'include',
            headers: { Authorization: `Bearer ${newSession.access_token}` },
          }).catch(() => {});

          // Best-effort, write-once geography capture: send the browser timezone
          // (the server adds country from the Vercel edge geo header — no IP
          // stored). Fire-and-forget; idempotent server-side (only fills empty
          // columns), so re-firing on later SIGNED_IN events is a cheap no-op.
          try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
              fetch('/api/profile/geo', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${newSession.access_token}`,
                },
                body: JSON.stringify({ timezone }),
              }).catch(() => {});
            }
          } catch {
            // Intl unavailable — skip capture silently.
          }

          const authProvider = newSession.user.app_metadata?.provider;
          if (authProvider === 'google' || authProvider === 'apple') {
            void capturePendingOAuthAttribution(newSession.access_token);
          }
        }
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setError(null);
      });

      subscription = authSubscription;

      // Watchdog so a hung backend can't leave the UI stuck on a spinner.
      //
      // It must NOT assert "signed out" though. getSession() is not always
      // local — when the stored access token has expired it performs a network
      // refresh, which can exceed this budget on a slow connection. Clearing
      // session+user here resolved loading to a LIE: ProtectedRoute saw
      // resolved-and-unauthenticated and redirected a genuinely signed-in user
      // to /auth, moments before getSession() came back with their session.
      // That is the same shape as the recovery-link bounce fixed in
      // ResetPasswordConfirm — a guard acting on a loading flag that resolved
      // to the wrong answer.
      //
      // Now it only releases the spinner. Session/user are left untouched and
      // the real getSession()/onAuthStateChange result remains the only thing
      // that decides whether someone is signed in. The budget is also raised,
      // since 3s is inside the normal range for a token refresh on mobile.
      const sessionCheckTimeout = (sessionCheckTimeoutHandle = setTimeout(() => {
        setLoading(false);
        // Deliberately no setSession(null)/setUser(null) — see above.
      }, 8000));

      supabase.auth
        .getSession()
        .then(({ data: { session: currentSession } }) => {
          clearTimeout(sessionCheckTimeout);

          // Session retrieved successfully
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setError(null);
          setLoading(false);
        })
        .catch((err) => {
          clearTimeout(sessionCheckTimeout);
          logger.warn('Session check failed:', err.message);
          setSession(null);
          setUser(null);
          setLoading(false);
          // Don't set error here - user can still see login form and try to authenticate
          // Error will be shown when they actually try to login
        });
    } catch (err) {
      if (import.meta.env.DEV) {
        logger.warn('Auth initialization failed:', err);
      }
      // Continue without auth - user can still see the login form
      setSession(null);
      setUser(null);
      setLoading(false);
    }

    return () => {
      if (sessionCheckTimeoutHandle) clearTimeout(sessionCheckTimeoutHandle);
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  // Add a function to refresh user data
  const refreshUser = async (): Promise<void> => {
    try {
      const {
        data: { user: refreshedUser },
        error,
      } = await supabase.auth.getUser();
      if (error) {
        if (import.meta.env.DEV) {
          logger.error('Error refreshing user:', error);
        }
      } else {
        // User data refreshed successfully
        setUser(refreshedUser);
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        logger.error('Exception refreshing user:', err);
      }
    }
  };

  // Auth state monitoring removed for performance

  const signIn = async (
    email: string,
    password: string,
    captchaToken?: string,
  ): Promise<{ error: AuthError | null; data: Session | null }> => {
    // No client-side bypass: credentials must exist on whichever Supabase project VITE_SUPABASE_URL points to.
    // Local: supabase/seed.sql documents test@example.com / password123 after db reset. Hosted: create the user or use an account that exists there.

    const response = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });

    // Fallback logic removed to ensure strict reliance on Supabase client
    if (response.error && import.meta.env.DEV) {
      logger.error('Supabase login failed:', response.error);
    }

    // Sign in completed
    return {
      error: response.error,
      data: response.data.session,
    };
  };

  // Update signUp to accept options for metadata
  const signUp = async (
    email: string,
    password: string,
    options?: { data?: Record<string, JsonValue>; captchaToken?: string; emailRedirectTo?: string },
  ): Promise<{ error: AuthError | null; data: { user: User | null; session: Session | null } }> => {
    const { captchaToken, ...restOptions } = options || {};
    const launchAttribution = getBrowserLaunchAttribution();
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        ...restOptions,
        data: {
          ...(restOptions.data ?? {}),
          launch_attribution: launchAttribution,
        },
        ...(captchaToken ? { captchaToken } : {}),
      },
    });

    // Sign up completed
    return {
      error: response.error,
      data: {
        user: response.data.user,
        session: response.data.session,
      },
    };
  };

  const signOut = async (): Promise<void> => {
    // Sign out attempt
    await supabase.auth.signOut();
  };

  // Update function signature to accept options
  const resetPasswordForEmail = async (
    email: string,
    options?: { redirectTo?: string; captchaToken?: string },
  ): Promise<{ error: AuthError | null; data: { user: null } }> => {
    // Reset password attempt
    // Pass the received options (redirectTo + captchaToken) to Supabase.
    // Without captchaToken the endpoint 400s with `captcha_failed`.
    const response = await supabase.auth.resetPasswordForEmail(email, options);

    // Reset password completed
    return {
      error: response.error,
      data: {
        user: null,
      },
    };
  };

  const resendSignupConfirmation = async (
    email: string,
    options?: { captchaToken?: string; emailRedirectTo?: string },
  ): Promise<{ error: AuthError | null }> => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      ...(options ? { options } : {}),
    });
    return { error };
  };

  const updatePassword = async (
    password: string,
  ): Promise<{ error: AuthError | null; data: { user: User | null } }> => {
    // Update password attempt
    const response = await supabase.auth.updateUser({
      password: password,
    });

    // Update password completed
    return {
      error: response.error,
      data: {
        user: response.data.user,
      },
    };
  };

  // Add Google Sign In function
  const signInWithGoogle = async (): Promise<{ error: AuthError | null; data: null }> => {
    persistPendingOAuthAttribution();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    // Google sign in completed
    // The actual session/user update happens via onAuthStateChange after redirect
    return { error, data: null };
  };

  // Add Apple Sign In function
  const signInWithApple = async (): Promise<{ error: AuthError | null; data: null }> => {
    // Apple sign in attempt

    // Disable Apple OAuth in development if not configured to prevent test failures
    if (isDevelopment && import.meta.env.VITE_ENABLE_APPLE_AUTH !== 'true') {
      return {
        error: {
          message: 'Apple Sign In is not configured in development mode',
        } as AuthError,
        data: null,
      };
    }

    // Get current domain for the redirect
    const redirectTo = window.location.origin;
    // Using dynamic redirect URL

    persistPendingOAuthAttribution();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        // Use dynamic redirect based on current domain instead of hardcoded localhost
        redirectTo,
      },
    });
    // Apple sign in completed
    // The actual session/user update happens via onAuthStateChange after redirect
    return { error, data: null };
  };

  /**
   * Establish a session from a one-time token minted by the Shopify OAuth
   * callback. A merchant who installs from the App Store never sets a
   * password — the callback provisions their account server-side — so this is
   * the only way their browser can obtain a session.
   */
  const signInWithToken = async (
    tokenHash: string,
  ): Promise<{ error: AuthError | null; data: Session | null }> => {
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'magiclink',
      });
      if (verifyError) {
        return { error: verifyError, data: null };
      }
      return { error: null, data: data.session ?? null };
    } catch (err: unknown) {
      let errorMessage = 'Could not complete sign in.';
      if (err && typeof err === 'object' && 'message' in err) {
        errorMessage = (err as { message: string }).message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      if (import.meta.env.DEV) {
        logger.error('Token sign in error:', errorMessage);
      }
      return { error: { message: errorMessage } as AuthError, data: null };
    }
  };

  const value = useMemo<AuthContextType>(
    () => ({
      session,
      user,
      loading,
      error,
      signIn,
      signUp,
      signOut,
      resetPasswordForEmail,
      resendSignupConfirmation,
      updatePassword,
      signInWithGoogle,
      signInWithApple,
      signInWithToken,
      refreshUser,
    }),
    [session, user, loading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Removed useAuth hook definition (moved to src/hooks/use-auth.ts)
